/**
 * Popup content component for event markers on the passage map
 */

import { EventPopupContent } from './EventPopupContent';
import { useMemo } from 'react';
import { usePlacesData } from '@/data/places/places-data-context';
import { findPlaceForEntity } from '@/data/places/place-images-fetch';
import { getStandardPeriod, PERIOD_DEFINITIONS } from '@/lib/utils/historical-periods-shared';

interface EventMarkerPopupProps {
    event: HistoricalEventByPassage | HistoricalEvent;
    historicalEvents: HistoricalEvent[]; // All events to search for alternative dates
    bookName?: string;
    matchingPlace: GeographicEntity;
    passageYear?: number | null;
}

export function EventMarkerPopup({ event, historicalEvents, bookName, matchingPlace, passageYear }: EventMarkerPopupProps) {
    const { getPlaceByName } = usePlacesData();
    // Look up the full event data only when popup is rendered
    const fullEvent = useMemo(() => {
        return historicalEvents.find(he => he.title === event.title);
    }, [historicalEvents, event.title]);

    // Combine sources from both event and fullEvent
    const combinedSources = useMemo(() => {
        const sources = [...(event.sources || [])];
        if (fullEvent?.sources) {
            sources.push(...fullEvent.sources);
        }
        return sources.length > 0 ? sources : undefined;
    }, [event.sources, fullEvent?.sources]);

    // Get place data and select appropriate reconstruction image
    const placeImageData = useMemo(() => {
        const place = findPlaceForEntity(matchingPlace, getPlaceByName);
        if (!place?.images?.reconstructionsByPeriod) {
            return { ruinsImageUrl: place?.images?.ruinsImageUrl, reconstructionImageUrl: undefined };
        }

        const availablePeriods = Object.keys(place.images.reconstructionsByPeriod) as HistoricalPeriod[];
        if (availablePeriods.length === 0) {
            return { ruinsImageUrl: place.images.ruinsImageUrl, reconstructionImageUrl: undefined };
        }

        // Select reconstruction based on passage year
        let selectedReconstructionUrl: string | undefined;

        if (passageYear !== null && passageYear !== undefined) {
            const matchingPeriod = getStandardPeriod(passageYear);

            // Try exact match first
            if (matchingPeriod && availablePeriods.includes(matchingPeriod)) {
                selectedReconstructionUrl = place.images.reconstructionsByPeriod[matchingPeriod]?.imageUrl;
            } else {
                // Find the closest available period to the passage year
                let closestPeriod: HistoricalPeriod | null = null;
                let smallestDiff = Infinity;

                availablePeriods.forEach(period => {
                    const periodDef = PERIOD_DEFINITIONS[period];
                    if (!periodDef) return;

                    // Calculate distance from passage year to this period's range
                    let diff: number;
                    if (passageYear < periodDef.start) {
                        // Passage year is before this period
                        diff = periodDef.start - passageYear;
                    } else if (passageYear > periodDef.end) {
                        // Passage year is after this period
                        diff = passageYear - periodDef.end;
                    } else {
                        // Passage year is within this period (shouldn't happen since we checked exact match)
                        diff = 0;
                    }

                    if (diff < smallestDiff) {
                        smallestDiff = diff;
                        closestPeriod = period;
                    }
                });

                if (closestPeriod) {
                    selectedReconstructionUrl = place.images.reconstructionsByPeriod[closestPeriod]?.imageUrl;
                }
            }
        }

        // Final fallback: Most recent period (if no passage year provided)
        if (!selectedReconstructionUrl) {
            const sortedPeriods = availablePeriods.sort((a, b) => {
                const aEnd = PERIOD_DEFINITIONS[a]?.end || 0;
                const bEnd = PERIOD_DEFINITIONS[b]?.end || 0;
                return bEnd - aEnd;
            });
            selectedReconstructionUrl = place.images.reconstructionsByPeriod[sortedPeriods[0]]?.imageUrl;
        }

        return {
            ruinsImageUrl: place.images.ruinsImageUrl,
            reconstructionImageUrl: selectedReconstructionUrl
        };
    }, [matchingPlace, getPlaceByName, passageYear]);

    // Get the full Place data for the place details section
    const place = useMemo(() => {
        return findPlaceForEntity(matchingPlace, getPlaceByName);
    }, [matchingPlace, getPlaceByName]);

    return (
        <EventPopupContent
            title={event.title}
            summary={event.summary}
            description={event.description}
            relevance={event.relevance}
            date={event.date}
            alternativeDates={event.alternativeDates || fullEvent?.alternativeDates}
            categories={event.categories}
            confidence={(event as any).confidence}
            sources={combinedSources}
            verseRef={(event as any).verseRef}
            bookName={bookName}
            placeName={matchingPlace.placeName}
            ruinsImageUrl={placeImageData.ruinsImageUrl}
            reconstructionImageUrl={placeImageData.reconstructionImageUrl}
            placeEntity={matchingPlace}
            placeData={place}
            passageYear={passageYear}
            eventImageUrl={fullEvent?.image?.imageUrl}
            generatedBy={(event as any).generatedBy || fullEvent?.generatedBy || (event as any).source}
        />
    );
}

